import React, { useState, useRef } from 'react';
import { Upload, File, X, Loader2, AlertCircle } from 'lucide-react';
import { BaseQuestion } from './types';
import { publicFormApi } from '../../api/publicForms';
import { useParams } from 'react-router-dom';
import { QuestionFooter } from './QuestionHelpers';
import { useRegistrationContext } from '../../portal_registration/contexts';
import { registrationFileUploadApi } from '../../portal_registration/api/fileUploadApi';

interface FileUploadQuestionProps {
  question: BaseQuestion;
  value: any;
  onChange: (value: any) => void;
  onBlur: () => void;
  error?: string;
  primaryColor: string;
  moreInfo?: string;
  isMoreInfoExpanded?: boolean;
  onToggleMoreInfo?: () => void;
}

export const FileUploadQuestion: React.FC<FileUploadQuestionProps> = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  primaryColor,
  moreInfo,
  isMoreInfoExpanded,
  onToggleMoreInfo,
}) => {
  const { formSlug } = useParams<{ formSlug: string }>();
  const { orderToken, isRegistrationContext } = useRegistrationContext();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File) => {
    // Clear previous errors
    setUploadError(null);

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload PDF, Word, Excel, or image files.');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB.');
      return;
    }

    setIsUploading(true);

    try {
      let presignedData;
      let uploadResult;

      // Determine which API to use based on context
      if (isRegistrationContext && orderToken) {
        // Use registration API for registration survey context
        presignedData = await registrationFileUploadApi.getUploadUrl(
          orderToken,
          file.name,
          file.type
        );
        uploadResult = await registrationFileUploadApi.uploadFile(presignedData, file);
      } else {
        // Use public form API for form context
        presignedData = await publicFormApi.getUploadUrl(
          formSlug || '',
          file.name,
          file.type
        );
        uploadResult = await publicFormApi.uploadFile(presignedData, file);
      }

      // Store the file information
      onChange({
        fileKey: uploadResult.key,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      onBlur();
    } catch (err) {
      console.error('File upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && !isUploading) {
      await processFile(files[0]);
    }
  };

  const removeFile = () => {
    onChange(null);
  };

  const handleZoneClick = () => {
    // Trigger file input click when clicking on the drop zone
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!question.answererCanUploadFiles) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">File upload is not enabled for this question.</p>
      </div>
    );
  }

  if (value && (value.fileName || value.name)) {
    const fileName = value.fileName || value.name;
    const fileSize = value.fileSize || value.size || 0;

    return (
      <div className="border-2 border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <File className="h-8 w-8 text-gray-400" />
            <div>
              <p className="font-medium text-sm text-gray-900">{fileName}</p>
              <p className="text-xs text-gray-500">
                {(fileSize / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={removeFile}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
          isDragActive
            ? 'bg-blue-50'
            : error
            ? 'border-red-500 hover:bg-red-50'
            : 'border-gray-300 hover:bg-gray-50'
        }`}
        style={isDragActive ? { borderColor: primaryColor } : undefined}
        onClick={handleZoneClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="space-y-1 text-center">
          {isUploading ? (
            <Loader2 className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          ) : (
            <Upload className={`mx-auto h-12 w-12 ${
              isDragActive ? 'text-blue-500' : error ? 'text-red-400' : 'text-gray-400'
            }`} />
          )}
          <div className="flex text-sm text-gray-600">
            <p>
              <span className="font-medium" style={{ color: error ? '#EF4444' : primaryColor }}>
                Click to upload
              </span>
              {' or drag and drop'}
            </p>
          </div>
          <input
            ref={fileInputRef}
            id={question.id}
            type="file"
            className="sr-only"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
            disabled={isUploading}
          />
          <p className="text-xs text-gray-500">
            PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF up to 10MB
          </p>
        </div>
      </div>
      <QuestionFooter
        error={error || uploadError || undefined}
        helpText={question.description}
        moreInfo={moreInfo}
        isMoreInfoExpanded={isMoreInfoExpanded}
        onToggleMoreInfo={onToggleMoreInfo}
      />
    </div>
  );
};